// The wire of the vault as the console's secrets surface speaks it: the
// operations, their subjects and bodies, the error codes, and a client that
// stores, deletes and lists, every request and reply sealed
// (02-DESIGN/02-secrets/01-the-vault.md § subjects and operations, § access).
// The admin template admits store, delete and list and never a read of a
// value; nothing here reads one.
import { base64Encode, utf8Encode } from "./encoding.js";
import { request } from "./request.js";
import { client, Family, SEP, validToken } from "./subject.js";
/** SERVICE is the vault's micro service name. */
export const SERVICE = "vault";
/** The nine operations. */
export const Op = {
    store: "store",
    get: "get",
    delete: "delete",
    list: "list",
    generate: "generate",
    sign: "sign",
    seal: "seal",
    open: "open",
    publicKey: "public-key",
};
/** OPS lists the nine operations. */
export const OPS = Object.values(Op);
/** validOp reports whether s is one of the nine. */
export function validOp(s) {
    return OPS.includes(s);
}
/** Error codes, uniform whatever the path. */
export const Code = {
    badRequest: "bad-request",
    cannotOpen: "cannot-open",
    conflict: "conflict",
    wrongKind: "wrong-kind",
    failed: "failed",
};
/** MAX_VALUE bounds a stored value; the client refuses an over-limit value before it is published. */
export const MAX_VALUE = 64 * 1024;
/** PathError is thrown for a path with an element that is not a subject token. */
export class PathError extends Error {
    path;
    constructor(path) {
        super("vault: path element is not a subject token");
        this.name = "PathError";
        this.path = path;
    }
}
/** validPath reports whether every dot-separated element of path is a subject token. */
export function validPath(path) {
    if (path === "") {
        return false;
    }
    return path.split(SEP).every(validToken);
}
/** subject builds `vault.<op>.<path>`; `vault.list` with an empty path is the bare list. */
export function subject(op, path) {
    if (!validOp(op)) {
        throw new Error(`vault: unknown operation "${op}"`);
    }
    if (path === "") {
        if (op === Op.list) {
            return client(Family.vault, op);
        }
        throw new PathError(path);
    }
    if (!validPath(path)) {
        throw new PathError(path);
    }
    return client(Family.vault, op, path);
}
/** workloadPath is a workload's own prefix in its tenant's vault: `workload.<id>.<name>`. */
export function workloadPath(id, ...rest) {
    return ["workload", id, ...rest].join(SEP);
}
/**
 * SOLUTION_PREFIX roots a solution's own prefix in the workspace's vault:
 * `solution.<name>.<secret>` for what an admin stores, `solution.<name>.keys.<key>`
 * for the keys it generates (02-DESIGN/14-solutions/02-authority.md § where a
 * solution's things live).
 */
export const SOLUTION_PREFIX = "solution";
/** solutionPath is a path under a solution's own prefix: `solution.<name>.<rest…>`. */
export function solutionPath(name, ...rest) {
    return [SOLUTION_PREFIX, name, ...rest].join(SEP);
}
/**
 * PERSON_PREFIX roots a person's own namespace in their tenant's vault
 * (02-DESIGN/02-secrets/01-the-vault.md § access; 02-DESIGN/08-toolkits/03-linking.md
 * § the namespace): `person.<principal>.>`, written by the person over their
 * own connection and read by the toolkit each credential is for.
 */
export const PERSON_PREFIX = "person";
/** personPath is a path in a person's own namespace: `person.<principal>.<rest…>`, the principal being their principal token. */
export function personPath(principal, ...rest) {
    return [PERSON_PREFIX, principal, ...rest].join(SEP);
}
/**
 * Client speaks the admin's operations to the vault of the account the
 * connection is placed in, every request and reply sealed.
 */
export class Client {
    nc;
    caller;
    timeout;
    /** A client over a connection with the caller's ephemeral key. */
    constructor(nc, caller, opts = {}) {
        this.nc = nc;
        this.caller = caller;
        this.timeout = opts.timeout ?? 5000;
    }
    // A path that is not one is refused as a rejection, like every other refusal.
    // The options' headers ride beside the sealing header: the carrier every
    // request a placed connection sends must bear.
    async call(op, path, body, opts) {
        const subj = subject(op, path);
        return await request(this.nc, this.caller, SERVICE, subj, body, {
            timeout: opts.timeout ?? this.timeout,
            headers: opts.headers,
        });
    }
    /**
     * store stores a value as a new version. expectedVersion, when given, guards
     * the write: a `conflict` means another writer went first. An over-limit
     * value is refused before anything is published.
     */
    store(path, value, expectedVersion, opts = {}) {
        const bytes = typeof value === "string" ? utf8Encode(value) : value;
        if (bytes.length > MAX_VALUE) {
            return Promise.reject(new RangeError(`vault: value over ${MAX_VALUE} bytes`));
        }
        const body = { value: base64Encode(bytes) };
        if (expectedVersion !== undefined) {
            body.expected_version = expectedVersion;
        }
        return this.call(Op.store, path, body, opts);
    }
    /** delete deletes the latest version, or every version. Neither is reversible. */
    delete(path, all = false, opts = {}) {
        const body = all ? { all_versions: true } : {};
        return this.call(Op.delete, path, body, opts);
    }
    /** list lists the paths under a prefix with versions and times, never values; an empty prefix lists every path. */
    list(prefix = "", opts = {}) {
        return this.call(Op.list, prefix, undefined, opts);
    }
}
//# sourceMappingURL=vault.js.map